import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined';
import { loadCoperReservation } from '../../../../api/endpoints/coper';
import type { HotelPassengerDetail } from '../../../../api/types/hotel';
import { ApiErrorAlert } from '../../../../components/ApiErrorAlert';

const money = (n?: number | null) =>
  n == null ? '—' : n.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const day = (s?: string | null) => (s && dayjs(s).isValid() ? dayjs(s).format('DD.MM.YYYY') : s || '—');

function Field({ label, value }: { label: string; value: ReactNode }) {
  return (
    <Grid size={{ xs: 12, sm: 6 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        {label}
      </Typography>
      <Typography variant="body2">{value ?? '—'}</Typography>
    </Grid>
  );
}

/**
 * `Hotel/ReservationLoad` detay diyaloğu — bir Coper rezervasyon satırına tıklanınca PNR
 * ile açılır. "Yeniden Export" butonu üst sayfaya devreder (`Hotel/ReExportCoper` bir
 * Hangfire job id'si ister; bu listede/detayda o alan yok — operatör job id'yi elle girer).
 */
export function CoperReservationDetailDialog({
  pnrNo,
  onClose,
  onReExport,
}: {
  pnrNo: string | null;
  onClose: () => void;
  onReExport: () => void;
}) {
  const query = useQuery({
    queryKey: ['coper', 'reservationLoad', pnrNo],
    queryFn: () => loadCoperReservation(pnrNo!),
    enabled: !!pnrNo,
  });
  const d = query.data;

  return (
    <Dialog open={!!pnrNo} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Rezervasyon Detayı — PNR {pnrNo}</DialogTitle>
      <DialogContent dividers>
        {query.isFetching && <CircularProgress size={24} />}
        {query.isError && <ApiErrorAlert error={query.error} />}
        {query.isSuccess && d && (
          <Stack spacing={2}>
            <Box>
              <Typography variant="subtitle1">{d.hotelName ?? '—'}</Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 0.5, flexWrap: 'wrap' }}>
                {d.reservationCode && <Chip size="small" label={`Rez. Kodu: ${d.reservationCode}`} />}
                {d.star && <Chip size="small" label={`${d.star} yıldız`} />}
                {typeof d.returnStatus === 'number' && (
                  <Chip size="small" label={`Durum: ${d.returnStatus}`} />
                )}
              </Stack>
            </Box>

            <Divider />
            <Typography variant="subtitle2">Oda &amp; Konaklama</Typography>
            <Grid container spacing={2}>
              <Field label="Oda" value={d.roomName} />
              <Field label="Pansiyon" value={d.boardName} />
              <Field label="Konaklama tipi" value={d.accom} />
              <Field label="Ünite" value={d.unit || '—'} />
              <Field label="Giriş" value={`${day(d.startDate)}${d.checkInTime ? ` ${dayjs(d.checkInTime).format('HH:mm')}` : ''}`} />
              <Field label="Çıkış" value={`${day(d.endDate)}${d.checkOutTime ? ` ${dayjs(d.checkOutTime).format('HH:mm')}` : ''}`} />
              <Field label="Yetişkin / Çocuk" value={`${d.adultCount ?? 0} / ${d.childCount ?? 0}`} />
              <Field label="Çocuk yaşları" value={d.childAges} />
            </Grid>

            <Divider />
            <Typography variant="subtitle2">Misafir &amp; İletişim</Typography>
            <Grid container spacing={2}>
              <Field label="Ad Soyad" value={[d.name, d.surName].filter(Boolean).join(' ') || '—'} />
              <Field label="E-posta" value={d.mail} />
              <Field label="Telefon" value={d.phone} />
              <Field label="Adres" value={[d.address, d.city, d.country].filter(Boolean).join(', ') || '—'} />
            </Grid>
            {!!d.passengers?.length && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Yolcular
                </Typography>
                <Stack spacing={0.5}>
                  {d.passengers.map((p: HotelPassengerDetail, idx: number) => (
                    <Typography key={idx} variant="body2">
                      {[p.name, p.surName].filter(Boolean).join(' ') || `Yolcu ${idx + 1}`}
                    </Typography>
                  ))}
                </Stack>
              </Box>
            )}

            <Divider />
            <Typography variant="subtitle2">Fiyat</Typography>
            <Grid container spacing={2}>
              <Field label="Satış fiyatı" value={`${money(d.servicePrice)} ${d.currencyType ?? ''}`} />
              <Field label="Arkman fiyatı" value={`${money(d.arkmanPrice)} ${d.currencyType ?? ''}`} />
            </Grid>

            {d.note && (
              <>
                <Divider />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Not
                  </Typography>
                  <Typography variant="body2">{d.note}</Typography>
                </Box>
              </>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button startIcon={<ReplayOutlinedIcon />} onClick={onReExport}>
          Yeniden Export
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose}>Kapat</Button>
      </DialogActions>
    </Dialog>
  );
}
